import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import EditMailSequenceEntryModal from '@/components/admin/academy/EditMailSequenceEntryModal';
import { faArrowLeft, faSpinner, faPlus, faTrash, faEdit, faEnvelope, faSave, faCheckCircle, faTimesCircle } from '@/fontawesome';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import type { Course, CourseMailSequence, CourseMailSequenceEntry, CourseID, VideoID, Video } from '@/models';

export default function CourseMailSequence() {
  const { courseId } = useParams<{ courseId: CourseID }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [mailSequence, setMailSequence] = useState<CourseMailSequence | null>(null);
  const [allVideos, setAllVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CourseMailSequenceEntry | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (courseId) {
      fetchData();
    }
  }, [courseId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const [courseData, sequenceData, modulesData] = await Promise.all([
        APIRoutes.getCourse(courseId!),
        APIRoutes.getCourseMailSequence(courseId!),
        APIRoutes.listModules(courseId!)
      ]);

      setCourse(courseData);
      setMailSequence(sequenceData);

      // Lade Videos für alle Module
      const videoPromises = (modulesData.modules || []).map(module =>
        APIRoutes.listVideos(module.id)
      );

      const videoResponses = await Promise.all(videoPromises);
      const allVideosArray: Video[] = [];

      // Sammle alle Videos in der richtigen Reihenfolge (nach Modul)
      videoResponses.forEach(response => {
        if (response.videos) {
          allVideosArray.push(...response.videos);
        }
      });

      setAllVideos(allVideosArray);
    } catch (err) {
      console.error('Error loading course mail sequence:', err);
      setError('Fehler beim Laden der Mail-Sequenz');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const validateSequence = (): string[] => {
    if (!mailSequence) return [];

    const errors: string[] = [];
    const ids = new Set<string>();

    mailSequence.sequence.forEach((entry, index) => {
      const position = `Eintrag ${index + 1}`;

      // ID-Validierung
      if (!entry.id || entry.id.trim() === '') {
        errors.push(`${position}: ID ist leer`);
      } else {
        if (ids.has(entry.id)) {
          errors.push(`${position}: ID "${entry.id}" ist bereits verwendet`);
        }
        ids.add(entry.id);
      }

      // E-Mail Betreff-Validierung
      if (!entry.emailSubject || entry.emailSubject.trim() === '') {
        errors.push(`${position}: E-Mail Betreff ist leer`);
      }

      // E-Mail Inhalt-Validierung
      if (!entry.emailBody || entry.emailBody.trim() === '') {
        errors.push(`${position}: E-Mail Inhalt ist leer`);
      }
    });

    return errors;
  };

  const handleSave = async () => {
    if (!mailSequence) return;

    // Validierung vor dem Speichern
    const validationErrors = validateSequence();
    if (validationErrors.length > 0) {
      const errorMessage = `Bitte korrigieren Sie folgende Fehler:\n\n${validationErrors.join('\n')}`;
      alert(errorMessage);
      return;
    }

    try {
      setSaving(true);
      const updatedSequence = await APIRoutes.updateCourseMailSequence(courseId!, {
        enabled: mailSequence.enabled,
        sequence: mailSequence.sequence
      });
      setMailSequence(updatedSequence);
      showSuccessToast('Mail-Sequenz erfolgreich gespeichert');
    } catch (err) {
      console.error('Error saving mail sequence:', err);
      showErrorToast('Fehler beim Speichern der Mail-Sequenz');
      handleAPIError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = () => {
    if (!mailSequence) return;
    setMailSequence({
      ...mailSequence,
      enabled: !mailSequence.enabled
    });
  };

  const handleAddEntry = () => {
    if (!mailSequence) return;

    // Generiere neue ID im Format SEQ_XXX
    let newId = '';

    if (mailSequence.sequence.length === 0) {
      // Keine Einträge vorhanden, starte mit SEQ_001
      newId = 'SEQ_001';
    } else {
      // Finde die höchste SEQ_XXX Nummer
      let highestNumber = 0;

      for (const entry of mailSequence.sequence) {
        const match = entry.id.match(/^SEQ_(\d{3})$/);
        if (match) {
          const number = parseInt(match[1], 10);
          if (number > highestNumber) {
            highestNumber = number;
          }
        }
      }

      if (highestNumber > 0) {
        // Erhöhe die höchste gefundene Nummer
        const nextNumber = highestNumber + 1;
        if (nextNumber <= 999) {
          newId = `SEQ_${nextNumber.toString().padStart(3, '0')}`;
        }
        // Bei Überlauf (>999) bleibt newId leer
      } else {
        // Keine gültigen SEQ_XXX IDs gefunden, starte mit SEQ_001
        newId = 'SEQ_001';
      }
    }

    const newEntry: CourseMailSequenceEntry = {
      id: newId,
      previousVideo: null,
      nextVideo: null,
      delayAfterPreVideo: 1,
      delayAfterCourseStart: 1,
      emailSubject: '',
      emailBody: ''
    };

    setMailSequence({
      ...mailSequence,
      sequence: [...mailSequence.sequence, newEntry]
    });
  };

  const handlePopulateList = () => {
    if (!mailSequence || !allVideos.length) return;

    // Warnung wenn bereits Einträge vorhanden sind
    if (mailSequence.sequence.length > 0) {
      if (!confirm(`Dies wird alle ${mailSequence.sequence.length} vorhandenen Einträge ersetzen. Möchten Sie fortfahren?`)) {
        return;
      }
    }

    // Videos sind bereits in der richtigen Reihenfolge (nach Modul und Position)
    const sortedVideos = [...allVideos];

    const newEntries: CourseMailSequenceEntry[] = sortedVideos.map((_, index) => {
      const previousVideo = index > 0 ? sortedVideos[index - 1].id : null;
      const nextVideo = index < sortedVideos.length - 1 ? sortedVideos[index + 1].id : null;

      // Generiere SEQ_XXX IDs
      const sequenceNumber = index + 1;
      const sequenceId = `SEQ_${sequenceNumber.toString().padStart(3, '0')}`;

      return {
        id: sequenceId,
        previousVideo,
        nextVideo,
        delayAfterPreVideo: 1,
        delayAfterCourseStart: index + 1, // Startet bei 1 und zählt hoch
        emailSubject: ``,
        emailBody: ``
      };
    });

    setMailSequence({
      ...mailSequence,
      sequence: newEntries
    });

    showSuccessToast(`${newEntries.length} Einträge automatisch erstellt`);
  };

  const handleDeleteEntry = (entryId: string) => {
    if (!mailSequence) return;

    setMailSequence({
      ...mailSequence,
      sequence: mailSequence.sequence.filter(entry => entry.id !== entryId)
    });
  };

  const handleUpdateEntry = (entryId: string, updates: Partial<CourseMailSequenceEntry>) => {
    if (!mailSequence) return;

    setMailSequence({
      ...mailSequence,
      sequence: mailSequence.sequence.map(entry =>
        entry.id === entryId ? { ...entry, ...updates } : entry
      )
    });
  };

  const moveEntry = (fromIndex: number, toIndex: number) => {
    if (!mailSequence) return;

    const newSequence = [...mailSequence.sequence];
    const [movedEntry] = newSequence.splice(fromIndex, 1);
    newSequence.splice(toIndex, 0, movedEntry);

    setMailSequence({
      ...mailSequence,
      sequence: newSequence
    });
  };

  const handleEditEmail = (entry: CourseMailSequenceEntry) => {
    setEditingEntry(entry);
    setIsEditModalOpen(true);
  };

  const handleSaveEmail = (entryId: string, updates: { emailSubject: string; emailBody: string }) => {
    handleUpdateEntry(entryId, updates);
    setIsEditModalOpen(false);
    setEditingEntry(null);
  };

  if (criticalError) {
    return (
      <FullscreenError
        title="Mail-Sequenz nicht verfügbar"
        message={error || 'Die Mail-Sequenz konnte nicht geladen werden.'}
        onRetry={fetchData}
      />
    );
  }

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      </>
    );
  }

  if (!course || !mailSequence) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Kurs oder Mail-Sequenz nicht gefunden</p>
        </div>
      </>
    );
  }

  // Videos sind aus Modules geladen, nicht aus dem mailSequence lookup

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/courses"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Mail-Sequenz
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {course.title}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleToggleEnabled}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                mailSequence.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              <FontAwesomeIcon
                icon={mailSequence.enabled ? faCheckCircle : faTimesCircle}
                className="w-5 h-5"
              />
              <span>{mailSequence.enabled ? 'Aktiviert' : 'Deaktiviert'}</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={saving ? faSpinner : faSave} className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
              <span>{saving ? 'Speichern...' : 'Speichern'}</span>
            </button>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6">
          <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Mail-Sequenz Logik</h3>
          <div className="text-blue-700 dark:text-blue-300 text-sm space-y-1">
            <p>• Ein Benutzer erhält eine E-Mail entweder <strong>[x]</strong> Tage nach Kursbeginn</p>
            <p>• oder <strong>[y]</strong> Tage nachdem er das Video in "Vorheriges Video" beendet hat</p>
            <p>• (je nachdem, was zuerst eintritt)</p>
            <p></p>
            <p>Wenn der Benutzer bereits das "Nächste Video" gesehen hat, wird dieser Eintrag übersprungen</p>
            <p>Maximal 1 E-Mail pro Mail-Sequenz pro Tag</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Sequenz-Einträge ({mailSequence.sequence.length})
              </h2>
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {mailSequence.sequence.length === 0 ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faEnvelope} className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  Keine Einträge vorhanden
                </h3>
                <p className="text-gray-500 dark:text-gray-500 mb-4">
                  Fügen Sie Ihren ersten Mail-Sequenz-Eintrag hinzu.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={handleAddEntry}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer"
                  >
                    Ersten Eintrag erstellen
                  </button>
                  {allVideos.length > 0 && (
                    <button
                      onClick={handlePopulateList}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer"
                    >
                      Liste befüllen ({allVideos.length} Videos)
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Vorheriges Video
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Nächstes Video
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tage nach Video
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tage nach Start
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        E-Mail Betreff
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {mailSequence.sequence.map((entry, index) => {
                      const previousVideoTitle = entry.previousVideo
                        ? allVideos.find(v => v.id === entry.previousVideo)?.title || entry.previousVideo
                        : '(Kein Video)';
                      const nextVideoTitle = entry.nextVideo
                        ? allVideos.find(v => v.id === entry.nextVideo)?.title || entry.nextVideo
                        : '(Kein Video)';

                      // Validierung für visuelles Feedback
                      const hasEmptyId = !entry.id || entry.id.trim() === '';
                      const hasDuplicateId = mailSequence.sequence.filter(e => e.id === entry.id).length > 1;
                      const hasEmptySubject = !entry.emailSubject || entry.emailSubject.trim() === '';
                      const hasEmptyBody = !entry.emailBody || entry.emailBody.trim() === '';
                      const hasErrors = hasEmptyId || hasDuplicateId || hasEmptySubject || hasEmptyBody;

                      return (
                        <tr key={`${entry.id}-${index}`} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          hasErrors ? 'bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500' : ''
                        }`}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => moveEntry(index, Math.max(0, index - 1))}
                                disabled={index === 0}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 cursor-pointer"
                                title="Nach oben"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => moveEntry(index, Math.min(mailSequence.sequence.length - 1, index + 1))}
                                disabled={index === mailSequence.sequence.length - 1}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 cursor-pointer"
                                title="Nach unten"
                              >
                                ↓
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="text"
                              value={entry.id}
                              onChange={(e) => handleUpdateEntry(entry.id, { id: e.target.value.toUpperCase() })}
                              className={`w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 dark:bg-gray-700 dark:text-white uppercase ${
                                hasEmptyId || hasDuplicateId
                                  ? 'border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20'
                                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                              }`}
                              title={hasEmptyId ? 'ID ist erforderlich' : hasDuplicateId ? 'ID bereits verwendet' : ''}
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <select
                              value={entry.previousVideo || ''}
                              onChange={(e) => handleUpdateEntry(entry.id, {
                                previousVideo: e.target.value ? e.target.value as VideoID : null
                              })}
                              className="w-48 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                              title={previousVideoTitle}
                            >
                              <option value="">(Kein Video)</option>
                              {allVideos.map(video => (
                                <option key={video.id} value={video.id}>
                                  {video.title}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <select
                              value={entry.nextVideo || ''}
                              onChange={(e) => handleUpdateEntry(entry.id, {
                                nextVideo: e.target.value ? e.target.value as VideoID : null
                              })}
                              className="w-48 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                              title={nextVideoTitle}
                            >
                              <option value="">(Kein Video)</option>
                              {allVideos.map(video => (
                                <option key={video.id} value={video.id}>
                                  {video.title}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={entry.delayAfterPreVideo}
                              onChange={(e) => handleUpdateEntry(entry.id, {
                                delayAfterPreVideo: parseFloat(e.target.value) || 0
                              })}
                              className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={entry.delayAfterCourseStart}
                              onChange={(e) => handleUpdateEntry(entry.id, {
                                delayAfterCourseStart: parseFloat(e.target.value) || 0
                              })}
                              className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className={`max-w-xs truncate text-sm ${
                              hasEmptySubject || hasEmptyBody
                                ? 'text-red-600 dark:text-red-400 font-medium'
                                : 'text-gray-900 dark:text-white'
                            }`} title={entry.emailSubject || 'Kein Betreff'}>
                              {entry.emailSubject || <span className="italic">
                                {hasEmptySubject && hasEmptyBody ? 'Betreff und Inhalt fehlen' :
                                 hasEmptySubject ? 'Betreff fehlt' :
                                 hasEmptyBody ? 'Inhalt fehlt' : 'Kein Betreff'}
                              </span>}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {hasErrors && (
                                <span className="text-red-500" title="Validierungsfehler">
                                  <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4" />
                                </span>
                              )}
                              <button
                                onClick={() => handleEditEmail(entry)}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer"
                                title="E-Mail bearbeiten"
                              >
                                <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteEntry(entry.id)}
                                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 cursor-pointer"
                                title="Löschen"
                              >
                                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Eintrag hinzufügen Button am Ende */}
          {mailSequence.sequence.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={handleAddEntry}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer"
              >
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                <span>Eintrag hinzufügen</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Email Modal */}
      <EditMailSequenceEntryModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingEntry(null);
        }}
        entry={editingEntry}
        onSave={handleSaveEmail}
      />
    </>
  );
}
